// src/data/claimantRosterFacts.js
//
// Bob, 2026-10-17, filling a claim for a member: "this should already be entered in this claim when we started entering
// his personal, work, and union information ... how can we address this for the local chairs filling out the forms and
// their personal and personnel information. How can we automate this." Plan: data/YCP_PLAN_OVERTIME_CLAIM_FACTS_2026-10-17.md, B.
//
// The member's application already holds his roster number, seniority date and job symbol (applicationRecord.js
// BLOCKS.work), and his Local and General Chairperson may already read it (firestore.rules /applications: inLCScope,
// inGCScope, admin). This module says which query the signed-in officer may run - a query the rules will refuse is
// never attempted - and what the claim takes from the record found. Pure: no Firestore, no React.

const s = (v) => (v == null ? '' : String(v)).trim();
const OFFICER_ROLES = ['local_chair', 'general_chair', 'admin'];

// The constraints of the one query this user may run for a claimant's e-mail, or null (not an officer, no e-mail, or
// no scope to query within). Firestore requires a query's constraints to prove the read rule, so the LC query names
// his scopes and the GC query his committee; 'in' takes at most 30 values.
export function rosterQueryFor(user, claimantEmail) {
  const u = user || {};
  const email = s(claimantEmail).toLowerCase();
  if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return null;
  if (u.isAdmin || u.role === 'admin') return { email, scopeField: null, scopeValues: null };
  if (u.role === 'general_chair' && s(u.committee)) return { email, scopeField: 'generalCommittee', scopeValues: [s(u.committee)] };
  if (u.role === 'local_chair') {
    const scopes = (Array.isArray(u.scopes) ? u.scopes : []).map(s).filter(Boolean).slice(0, 30);
    return scopes.length ? { email, scopeField: 'scopeKey', scopeValues: scopes } : null;
  }
  return null;
}

// From the applications found, the one to use: approved first, then pending; the newest of those. Refused ones never.
export function pickApplication(apps) {
  const list = (Array.isArray(apps) ? apps : []).filter((a) => a && (a.status === 'approved' || a.status === 'pending'));
  const time = (a) => {
    const t = a.decidedAt || a.submittedAt;
    if (t && typeof t.toMillis === 'function') return t.toMillis();
    const d = Date.parse(s(t)); return Number.isFinite(d) ? d : 0;
  };
  list.sort((x, y) => (x.status === y.status ? time(y) - time(x) : (x.status === 'approved' ? -1 : 1)));
  return list[0] || null;
}

// What the claim takes from the application: only fields that are EMPTY on the claim, never over what was typed.
// Returns { patch, filled: [field labels], source: { name, status, on } } or null when there is nothing to take.
export const FROM_APPLICATION = [
  ['rosterNumber', 'rosterNumber', 'roster number'],
  ['seniorityDate', 'seniorityDate', 'seniority date'],
  ['claimantJobSymbol', 'jobSymbol', 'job symbol'],
  ['employeeNumber', 'employeeNumber', 'employee number'],
];
export function factsFromApplication(app, claim) {
  if (!app) return null;
  const c = claim || {};
  const patch = {}, filled = [];
  FROM_APPLICATION.forEach(([claimField, appField, label]) => {
    if (!s(c[claimField]) && s(app[appField])) { patch[claimField] = s(app[appField]); filled.push(label); }
  });
  const on = app.decidedAt || app.submittedAt;
  const onIso = on && typeof on.toDate === 'function' ? on.toDate().toISOString().slice(0, 10) : s(on).slice(0, 10);
  return { patch, filled, source: { name: s(app.fullLegalName), status: s(app.status), on: onIso } };
}

// The line the screen shows. Found and filled; found with nothing to fill; or not found - never a guess.
export function rosterNotice(result) {
  if (!result) return 'No application on record for this e-mail in your territory. Enter his roster facts.';
  const who = result.source.name || 'the member';
  const when = result.source.on ? ' of ' + result.source.on : '';
  const pend = result.source.status === 'pending' ? ' (not yet approved)' : '';
  if (!result.filled.length) return 'Found ' + who + '’s application' + when + pend + '. Its roster facts are already on this claim.';
  return 'Filled the ' + result.filled.join(', ') + ' from ' + who + '’s application' + when + pend + '. Check them.';
}

export function isOfficer(user) { const u = user || {}; return !!(u.isAdmin || OFFICER_ROLES.includes(u.role)); }
